import { useCallback, useRef, useState } from 'react';
import { type GitlabProvisioningManageResult } from 'network/httpClients/GitlabClient';
import { useGitlabProvisioningManageMutation } from 'network/mutations/useGitlabProvisioningManageMutation';
import { useGitlabProvisioningManageStatusQuery } from 'network/queries/useGitlabProvisioningManageStatusQuery';
import { GENERIC_PROVISIONING_ERROR_KEY } from '../consts';
import { type GitlabV2ManageDeltas } from '../types';
import { getProvisioningErrorCode } from '../utils';
import {
  computeManageOutcome,
  getFailedResults,
  getSucceededResults,
  type ManageRunOutcome,
} from './outcome';

/** Inputs the shared run needs to fire a `/manage` call for a provisioned install. */
export type GitlabManageRunParams = {
  installationId: string;
  /** Transient elevation PAT, re-sent per call. */
  accessToken: string;
};

/**
 * Maps a manage/install start error to an i18n error-key suffix. Install and
 * manage differ here: install re-validates the PAT up front so a typed whole-run
 * code can come back on the start call; manage carries the PAT statelessly, so a
 * start error is always the generic failure (typed PAT errors surface on the list
 * call instead).
 */
export type StartErrorKeyResolver = (error: unknown) => string;

/** Optimistic all-queued rows for the fired deltas, matching the backend's seed shape. */
const seedQueuedResults = (
  deltas: GitlabV2ManageDeltas,
): GitlabProvisioningManageResult[] => {
  const row = (
    id: number,
    kind: 'group' | 'project',
  ): GitlabProvisioningManageResult => ({
    scope: String(id),
    kind,
    status: 'queued',
    error: null,
  });

  return [
    ...deltas.remove.groupIds.map((id) => row(id, 'group')),
    ...deltas.remove.projectIds.map((id) => row(id, 'project')),
    ...deltas.add.groupIds.map((id) => row(id, 'group')),
    ...deltas.add.projectIds.map((id) => row(id, 'project')),
  ];
};

/**
 * The shared page-3 run lifecycle for the v2 GitLab wizard, used by both the
 * install step (add-only) and the manage modal (add + remove). Fires the `/manage`
 * mutation, polls status to a terminal state, derives the outcome from the
 * per-scope results, and exposes an idempotent Retry that replays the same
 * deltas. Owns none of the presentation — the caller renders `GitlabManageResults`
 * from the returned state.
 *
 * On `run` it seeds an optimistic all-queued list so the results page shows the
 * full selection immediately (no loader / layout shift); the poll then streams
 * each scope through queued→in_progress→terminal.
 *
 * @param params - installation id + PAT; undefined defers the run until ready.
 * @param resolveStartErrorKey - Maps a start-call error to an error-key suffix.
 * @returns The run state (outcome, results, fire/retry) for the results view.
 */
export const useGitlabManageRun = (
  params: GitlabManageRunParams | undefined,
  resolveStartErrorKey: StartErrorKeyResolver,
) => {
  const { mutate: startManage } = useGitlabProvisioningManageMutation();

  const [manageId, setManageId] = useState<string | undefined>();
  const [startErrorKey, setStartErrorKey] = useState<string | undefined>();
  const [seededResults, setSeededResults] = useState<
    GitlabProvisioningManageResult[]
  >([]);
  const firedDeltasRef = useRef<GitlabV2ManageDeltas | undefined>();

  const { data: status, isError: isStatusError } =
    useGitlabProvisioningManageStatusQuery(manageId);

  const run = useCallback(
    (deltas: GitlabV2ManageDeltas) => {
      if (params === undefined) return;

      setStartErrorKey(undefined);
      setManageId(undefined);
      setSeededResults(seedQueuedResults(deltas));
      firedDeltasRef.current = deltas;
      startManage(
        {
          installationId: params.installationId,
          accessToken: params.accessToken,
          add: deltas.add,
          remove: deltas.remove,
        },
        {
          onSuccess: (result) => setManageId(result.manageId),
          onError: (error) => setStartErrorKey(resolveStartErrorKey(error)),
        },
      );
    },
    [params, startManage, resolveStartErrorKey],
  );

  // Replay the exact deltas from the last run — the backend is idempotent
  // (already-applied scopes come back `skipped`), so Retry is safe.
  const retry = useCallback(() => {
    if (firedDeltasRef.current) run(firedDeltasRef.current);
  }, [run]);

  // The status query settles into an error only after its retries are exhausted,
  // so any error here is persistent — surface it as a generic, retryable failure.
  const transportErrorKey =
    startErrorKey ??
    (isStatusError ? GENERIC_PROVISIONING_ERROR_KEY : undefined);
  const outcome: ManageRunOutcome = computeManageOutcome(
    transportErrorKey,
    status,
  );

  // Terminal poll results are authoritative (even when empty — a whole-run
  // failure). Before that, prefer the poll's streamed results, falling back to the
  // optimistic seed so the list shows immediately with no loader gap.
  const results =
    outcome === 'pending'
      ? status?.results && status.results.length > 0
        ? status.results
        : seededResults
      : status?.results ?? [];

  return {
    run,
    retry,
    status,
    results,
    succeededResults: getSucceededResults(results),
    failedResults: getFailedResults(results),
    outcome,
    transportErrorKey,
    /** The whole-run failure code from a `failed` status (for whole-run copy). */
    wholeRunErrorCode: status?.error?.code,
  };
};

export type GitlabManageRunState = ReturnType<typeof useGitlabManageRun>;

/** Resolver: install re-validates the PAT, so a typed whole-run code may return. */
export const resolveInstallStartErrorKey =
  (
    mapCode: (code: string | null | undefined) => string,
  ): StartErrorKeyResolver =>
  (error) =>
    mapCode(getProvisioningErrorCode(error));
